import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFileCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';

interface CustomerUnavailableStateProps {
  title: string;
  description: string;
  /** The way out — normally "Back to …". Never a retry. */
  action?: ReactNode;
  icon?: IconDefinition;
  className?: string;
}

/**
 * D-130 Phase 6D — the PERMANENT counterpart to `CustomerErrorState`.
 *
 * `CustomerErrorState` says "something went wrong, try again". That is the
 * right answer for a timeout or a 5xx and the wrong answer for a 404 or a 403:
 * those outcomes are settled, and a retry button turns a clear refusal into a
 * loop the customer can never win.
 *
 * This surface therefore has NO retry. It states the outcome and offers a way
 * back to a list the customer can actually see.
 *
 * `role="status"` rather than `role="alert"`: nothing is broken, so this is an
 * announcement, not an alarm — but it must still be announced, because the
 * customer arrived here expecting a record.
 *
 * WORDING RULE: the description must be identical for "does not exist" and
 * "exists but belongs to another location". The server deliberately returns the
 * same 404 for both, and copy that distinguished them would hand back exactly
 * the existence signal the uniform response exists to withhold.
 */
export function CustomerUnavailableState({
  title,
  description,
  action,
  icon = faFileCircleQuestion,
  className = '',
}: CustomerUnavailableStateProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center gap-3 rounded-customer border border-customer-border bg-customer-surface px-4 py-10 text-center shadow-customer-sm',
        className,
      )}
    >
      <span
        className="flex h-12 w-12 items-center justify-center rounded-full bg-customer-text/[0.06] text-customer-text-secondary"
        aria-hidden="true"
      >
        <FontAwesomeIcon icon={icon} className="h-5 w-5" />
      </span>
      <p className="text-sm font-semibold text-customer-text">{title}</p>
      <p className="max-w-sm text-sm leading-5 text-customer-text-secondary">{description}</p>
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
